import {
  BAND_FLOOR,
  metricDependencies,
  type LocalizedText,
  type NpsBucket,
  type QuestionBinding,
  type ReportMetricDefinition,
} from '@arnold/core';
import type { TableColumn } from '../components/AnswerGroupTable/types';
import type { AnswerGroupRowMode } from '../types';
import type { AdHocGroupConfig } from '../filtering';

/** The metric contract (calculations, identifiers) lives in `@arnold/core`; layout, components and tables live here. */
export type {
  LocalizedText,
  MetricCalculation,
  MetricFormat,
  MetricRenderer,
  Normalization,
  NpsBucket,
  QuestionBinding,
  RespondentCondition,
  ReportMetricCalculation,
  ReportMetricDefinition,
  ScaleBand,
  ScaleSource,
  ScoreSource,
} from '@arnold/core';
export { BAND_FLOOR, METRIC_IDENTIFIER_MAX_LENGTH } from '@arnold/core';

/** Attrition-risk preset: two categories one deviation below the mean plus eNPS detractors. Labels are translated. */
export const ATTRITION_RISK_PRESET = {
  categories: [
    { id: 'workDedication', labelKey: 'definition.segmentPresets.workDedication' },
    { id: 'personalGrowth', labelKey: 'definition.segmentPresets.personalGrowth' },
  ],
  deviations: 1,
  npsConditionId: 'enpsDetractor',
  buckets: ['detractor'] satisfies NpsBucket[],
  labels: { matching: 'definition.segmentPresets.atRisk', other: 'definition.segmentPresets.notAtRisk' },
};
/** Default engagement bands: engaged ≥ 3.5, neutral ≥ 2.1 and < 3.5, disengaged < 2.1. Labels are translated. */
export const ENGAGEMENT_BANDS: { id: string; from: number; labelKey: string }[] = [
  { id: 'engaged', from: 3.5, labelKey: 'definition.bands.engaged' },
  { id: 'neutral', from: 2.1, labelKey: 'definition.bands.neutral' },
  { id: 'disengaged', from: BAND_FLOOR, labelKey: 'definition.bands.disengaged' },
];
export type ReportTableColumn = TableColumn & { translations?: LocalizedText };
export type ReportComparisonGroup =
  | { type: 'baseline'; id: string }
  | { type: 'custom'; label: LocalizedText; teamIds: string[]; answerSelections: AdHocGroupConfig['answerSelections'] };
export interface ReportTableConfiguration {
  columns: ReportTableColumn[];
  rowMode: AnswerGroupRowMode;
  pageSize: number;
  sortingEnabled: boolean;
  searchEnabled: boolean;
  columnOrderingEnabled: boolean;
  columnResizeEnabled: boolean;
  horizontalScroll: boolean;
  responseQuestionIndex?: string;
  nonEmptyTextColumnId?: string;
  /** Undefined follows header filters; an empty array is an intentionally empty comparison. */
  comparisonGroups?: ReportComparisonGroup[];
}
export type ReportComponent = { id: string; width?: 'full' | 'half' | 'third' | 'quarter' } & (
  | { type: 'section'; label: LocalizedText }
  | ({ type: 'question' } & QuestionBinding)
  | { type: 'responseRate' | 'anonymityRate' }
  | { type: 'aiSummary'; displayMode?: 'collapsible' | 'expanded' }
  | { type: 'metric'; metricId: string }
  | { type: 'table'; label: LocalizedText; table: ReportTableConfiguration }
);
export interface ReportDefinitionDocument {
  schemaVersion: 1;
  metrics: ReportMetricDefinition[];
  pages: { overview: { components: ReportComponent[] } };
}

export const localizedText = (text: LocalizedText | undefined, language: string): string =>
  text?.[language] ||
  text?.[language.split('-')[0]] ||
  Object.keys(text ?? {})
    .sort()
    .map((key) => text?.[key])
    .find(Boolean) ||
  '';

export const newDefinitionId = () => `r-${crypto.randomUUID()}`;

export function metricReferences(definition: ReportDefinitionDocument, id: string): string[] {
  return [
    ...definition.metrics
      .filter((metric) => metricDependencies(metric.calculation).includes(id))
      .map((metric) => metric.id),
    ...definition.pages.overview.components
      .filter(
        (component) =>
          (component.type === 'metric' && component.metricId === id) ||
          (component.type === 'table' &&
            component.table.columns.some((column) => 'metricId' in column && column.metricId === id)),
      )
      .map((component) => component.id),
  ];
}
